import React, { useEffect, useState } from "react";
import { collection, doc, getDocs, setDoc } from "firebase/firestore";
import { firestore } from "../../firebaseConfig";
import { Users as UsersIcon } from "lucide-react";
import toast from "react-hot-toast";

const roles = [
  { value: "CompanyAdmin", label: "Company Admin", description: "Full access to all company resources" },
  { value: "Manager", label: "Manager", description: "Second in command, can approve most operations" },
  { value: "Web Operator", label: "Web Operator", description: "Casual worker, needs approval for edits/deletes" },
  { value: "Auditor", label: "Auditor", description: "View and audit records, some operations need approval" },
];

const statusOptions = [
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "removed", label: "Removed" },
];

const mapStatusToEmployeeStatus = (status) => (status === "active" ? "approved" : status);
const REMOVED_EMPLOYEE_FIELDS_TO_CLEAR = {
  companyName: null,
  employeeRole: null,
  requestedCompany: null,
};

const AdminCompanyEmployees = ({ companyId }) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refreshEmployees = async () => {
    if (!companyId || !firestore) return;
    const employeesRef = collection(firestore, "companies", companyId, "employees");
    const employeesSnapshot = await getDocs(employeesRef);
    const employeesList = [];
    employeesSnapshot.forEach((doc) => {
      employeesList.push({ id: doc.id, ...doc.data() });
    });
    setEmployees(employeesList);
  };

  useEffect(() => {
    const fetchEmployees = async () => {
      if (!companyId || !firestore) return;
      setLoading(true);
      setError(null);
      try {
        await refreshEmployees();
      } catch (err) {
        console.error("Error fetching employees:", err);
        setError("Failed to load employees");
      } finally {
        setLoading(false);
      }
    };
    fetchEmployees();
  }, [companyId]);

  const handleUpdateEmployeeRole = async (employeeId, newRole) => {
    try {
      let permissions = {};
      switch (newRole) {
        case "CompanyAdmin":
          permissions = {
            canAddPayments: true,
            canEditRecords: true,
            canDeleteRecords: true,
            canApprovePayments: true,
            canViewReports: true,
            canManageSettings: true,
          };
          break;
        case "Manager":
          permissions = {
            canAddPayments: true,
            canEditRecords: true,
            canDeleteRecords: true,
            canApprovePayments: true,
            canViewReports: true,
            canManageSettings: true,
          };
          break;
        case "Web Operator":
          permissions = {
            canAddPayments: false,
            canEditRecords: false,
            canDeleteRecords: false,
            canApprovePayments: false,
            canViewReports: true,
            canManageSettings: false,
          };
          break;
        case "Auditor":
          permissions = {
            canAddPayments: false,
            canEditRecords: false,
            canDeleteRecords: false,
            canApprovePayments: false,
            canViewReports: true,
            canManageSettings: false,
          };
          break;
        default:
          permissions = {};
      }

      await setDoc(
        doc(firestore, "companies", companyId, "employees", employeeId),
        {
          role: newRole,
          permissions,
          updatedAt: Date.now(),
        },
        { merge: true }
      );

      await setDoc(
        doc(firestore, "users", employeeId),
        {
          employeeRole: newRole,
          updatedAt: Date.now(),
        },
        { merge: true }
      );

      toast.success("Employee role updated");
      await refreshEmployees();
    } catch (error) {
      console.error("Error updating role:", error);
      toast.error("Failed to update role");
    }
  };

  const handleUpdateEmployeeStatus = async (employeeId, newStatus) => {
    try {
      const userStatus = mapStatusToEmployeeStatus(newStatus);
      const userUpdate = {
        employeeStatus: userStatus,
        updatedAt: Date.now(),
      };

      if (newStatus === "removed") {
        Object.assign(userUpdate, REMOVED_EMPLOYEE_FIELDS_TO_CLEAR);
      }

      await setDoc(
        doc(firestore, "companies", companyId, "employees", employeeId),
        {
          status: newStatus,
          updatedAt: Date.now(),
        },
        { merge: true }
      );

      await setDoc(doc(firestore, "users", employeeId), userUpdate, { merge: true });

      toast.success("Employee status updated");
      await refreshEmployees();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  if (!companyId) {
    return <p className="text-sm text-slate-600">Select a company to manage employees.</p>;
  }

  if (loading) {
    return <p className="text-sm text-slate-600">Loading employees…</p>;
  }

  if (error) {
    return <p className="text-sm text-rose-600">{error}</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <UsersIcon className="h-5 w-5 text-blue-600" />
        <h3 className="text-base font-semibold text-slate-900">Manage Employees</h3>
      </div>

      {employees.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-6 text-center">
          <UsersIcon className="w-10 h-10 text-slate-400 mx-auto mb-2" />
          <p className="text-sm text-slate-600">No employees found for this company.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden border border-slate-200">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {employees.map((employee) => (
                <EmployeeRow
                  key={employee.id}
                  employee={employee}
                  roles={roles}
                  statusOptions={statusOptions}
                  onUpdateRole={handleUpdateEmployeeRole}
                  onUpdateStatus={handleUpdateEmployeeStatus}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const EmployeeRow = ({ employee, roles, statusOptions, onUpdateRole, onUpdateStatus }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newRole, setNewRole] = useState(employee.role);
  const [currentStatus, setCurrentStatus] = useState(employee.status || "active");
  const [statusUpdating, setStatusUpdating] = useState(false);

  useEffect(() => {
    setNewRole(employee.role);
    setCurrentStatus(employee.status || "active");
  }, [employee.role, employee.status]);

  const handleSave = () => {
    if (newRole !== employee.role) {
      onUpdateRole(employee.id, newRole);
    }
    setIsEditing(false);
  };

  const handleStatusChange = async (value) => {
    const previous = currentStatus;
    setStatusUpdating(true);
    try {
      await onUpdateStatus(employee.id, value);
      setCurrentStatus(value);
    } catch (error) {
      setCurrentStatus(previous);
    } finally {
      setStatusUpdating(false);
    }
  };

  const statusBadgeClass =
    currentStatus === "suspended"
      ? "bg-amber-100 text-amber-800"
      : currentStatus === "removed"
      ? "bg-red-100 text-red-800"
      : "bg-green-100 text-green-800";

  return (
    <tr>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-medium text-slate-900">{employee.fullName}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-slate-600">{employee.email}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {isEditing ? (
          <select
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
            className="text-sm px-2 py-1 border border-slate-300 rounded focus:ring-2 focus:ring-blue-500"
          >
            {roles.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>
        ) : (
          <span
            className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
              employee.role === "Manager"
                ? "bg-purple-100 text-purple-800"
                : employee.role === "Auditor"
                ? "bg-blue-100 text-blue-800"
                : employee.role === "CompanyAdmin"
                ? "bg-emerald-100 text-emerald-800"
                : "bg-gray-100 text-gray-800"
            }`}
          >
            {employee.role}
          </span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex flex-col gap-2">
          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusBadgeClass}`}>
            {currentStatus}
          </span>
          <select
            value={currentStatus}
            onChange={(e) => handleStatusChange(e.target.value)}
            disabled={statusUpdating}
            className="text-sm px-2 py-1 border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100"
          >
            {statusOptions.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        {isEditing ? (
          <div className="flex gap-2">
            <button onClick={handleSave} className="text-emerald-600 hover:text-emerald-900">
              Save
            </button>
            <button
              onClick={() => {
                setIsEditing(false);
                setNewRole(employee.role);
              }}
              className="text-slate-600 hover:text-slate-900"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button onClick={() => setIsEditing(true)} className="text-blue-600 hover:text-blue-900">
            Edit Role
          </button>
        )}
      </td>
    </tr>
  );
};

export default AdminCompanyEmployees;
